import {Link} from 'react-router';
import {useEffect, useState, useRef} from 'react';
import {Button, Card, Checkbox, Divider, Table, Row, Col, Popover, message} from 'antd';
import {
    getMomPlants, getPlants, getPlantTables, updatePlantTableName, insertPlantRow, getPlantRows,
    updatePlantRowName, updatePlantLocation, harvestPlant, deletePlantTable, deletePlantRow, transferPlant, updateBatch,
    getRoomBatch, insertRoomBatch, deleteRoomBatch
} from "../../api/Api";
import {toFSDateTime} from "../../util/timeutils";
import {getColumnSearchProps} from "../widget/SearchProps";
import {insertPlantTable} from "../../api/Api";
import {PlantStageType, PlantRoomType} from "../../api/Constants";
import {EmptyMetrcWidget} from "./EmptyMetrcWidget";
import {RoomNameWidget} from "./RoomNameWidget";
import {NameChangeWidget} from "./NameChangeWidget";
import {MovePlantWidget} from "./MovePlantWidget";
import {HarvestModal} from "./HarvestModal";
import {TrashWidget} from "../widget/TrashWidget";
import {PlantDropdownMenu} from "./PlantDropdownMenu";
import {RoomEnvironmentTab} from "./RoomEnvironmentTab";
import {RoomLightTab} from "./RoomLightTab";
import {TagSelectWidget} from "./TagSelectWidget";
import {MoistureWidget} from "./MoistureWidget";
import {TransferModal} from "./TransferModal";
import {TransferAllModal} from "./TransferAllModal";
import {getPlantRoomTransferOptions} from "./PlantTransferUtil";
import {handleNoSession} from "../../util/errorUtils";
import {NewPlant} from "./NewPlant";
import {BatchSelectWidget} from "../inventory/BatchSelectWidget";
import {DatePickerWidget} from "../widget/DatePickerWidget";

export const CurrentPlants = ({room, stage, roomMap, session, refresh, onRefresh, onApiFail, props}) => {
    const [plants, setPlants] = useState([]);
    const [checked, setChecked] = useState({});
    const io = {refresh, onRefresh, onApiFail};

    // harvest modal
    const [harvestModalOpen, setHarvestModalOpen] = useState(false);

    // harvest all
    const [harvestAllOpen, setHarvestAllOpen] = useState(false);

    // transfer modal
    const [transferModalOpen, setTransferModalOpen] = useState(false);

    // transfer all modal
    const [allModalOpen, setAllModalOpen] = useState(false);

    // tables
    const [tables, setTables] = useState([]);
    // rows
    const [rows, setRows] = useState([]);

    // focused plant
    const [focusPlant, setFocusPlant] = useState(undefined);

    // batch
    const [batch, setBatch] = useState(undefined);

    const [messageApi, contextHolder] = message.useMessage();

    const roomsMap = roomMap ? roomMap : {};

    useEffect(() => {
        if (room === undefined) {
            return;
        }

        const {id: roomId} = room;
        let promise;
        if (stage === PlantStageType.Mom) {
            promise = getMomPlants(true);
        } else {
            promise = getPlants(roomId);
        }
        promise.then(r => {
            setPlants(r.map(row => {
                return {
                    ...row,
                    key: row.id
                }
            }));
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });

        getPlantTables(room.id).then(r => {
            setTables(r.map(table => {
                return {
                    ...table,
                    key: table.id
                }
            }));
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });

        getPlantRows(room.id).then(r => {
            setRows(r.map(row => {
                return {
                    ...row,
                    key: row.id
                }
            }));
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });

        getRoomBatch(room.id).then(r => {
            setBatch(r);
        }).catch(e => {
            handleNoSession(e, onApiFail);
        })
    }, [stage, room, refresh, onApiFail]);

    const rowMap = {}
    rows.forEach(r => {
        rowMap[r.id] = r.id;
    });

    const cardStyle = {
        textAlign: 'left',
        marginTop: '10px',
        marginBottom: '10px'
    }

    const rowStyle = {
        marginTop: '5px',
        marginBottom: '5px'
    }

    const colStyle = {
        marginLeft: '5px',
        marginRight: '5px'
    }

    const onCheck = (id) => (e) => {
        const nextChecked = {...checked};
        nextChecked[id] = e.target.checked;
        setChecked(nextChecked);
    }

    const onSelectAll = () => {
        const nextChecked = {};
        for (const plant of plants) {
            nextChecked[plant.id] = true;
        }
        setChecked(nextChecked);
    }

    const onDeselectAll = () => {
        const nextChecked = {};
        for (const plant of plants) {
            nextChecked[plant.id] = false;
        }
        setChecked(nextChecked);
    }

    const onHarvestClick = (plant) => () => {
        setFocusPlant(plant);
        setHarvestModalOpen(true);
    }

    const onTransferModalOpen = (plant) => () => {
        setFocusPlant(plant)
        setTransferModalOpen(true);
    }

    const onTransferCancel = () => {
        setTransferModalOpen(false);
    }

    const onAllModalOpen = () => {
        setAllModalOpen(true);
    }

    const onAllModalCancel = () => {
        setAllModalOpen(false);
    }

    const searchInput = useRef(null);

    const columns = [
        {
            title: 'Select',
            key: 'select',
            dataIndex: 'id',
            render: (val, record) => {
                let select = checked[record.id] ? true : false;
                return <Checkbox checked={select} onClick={onCheck(record.id)} />
            }
        },
        {
            title: 'Name',
            key: 'name',
            dataIndex: 'name',
            ...getColumnSearchProps(`name`, searchInput),
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: (val, record) => {
                if (stage === PlantStageType.Mom) {
                    return <Link to={`/cultivationapp/mom/${record.id}`}>{val}</Link>
                } else {
                    return <Link to={`/cultivationapp/plant/${record.id}`}>{val}</Link>
                }
            }
        },
        {
            title: 'Description',
            key: 'description',
            dataIndex: 'description',
            ...getColumnSearchProps(`description`, searchInput)
        },
        {
            title: 'Stage',
            key: 'stage',
            dataIndex: 'stage',
            render: (val, record) => {
                return PlantStageType.Label[val];
            }
        },
        {
            title: 'Batch',
            key: 'batch',
            dataIndex: 'batchName',
            render: (val) => {
                return (val) ? val : 'None';
            }
        }
    ];
    if (stage > PlantStageType.Mom) {
        columns.push({
            title: 'Count',
            key: 'count',
            dataIndex: 'currentCount',
        });
    }

    columns.push(
        {
            title: 'Metrc',
            key: 'metricTag',
            dataIndex: 'metricTag',
            ...getColumnSearchProps(`metricTag`, searchInput),
            sorter: (a, b) => a.metricTag.localeCompare(b.metricTag),
            render: (val, record, index) => {
                if ((val === null) || (val ==="")) {
                    return <EmptyMetrcWidget plantId={record.id} />
                } else {
                    return val;
                }
            }
        },
        {
            title: 'Created',
            key: 'creationTime',
            dataIndex: 'creationTime',
            render:(val) => {
                return toFSDateTime(new Date(val));
            },
            sorter: (a, b) => (new Date(a.creationTime))- (new Date(b.creationTime)),
        },
    );

    if (stage !== PlantStageType.Mom) {
        columns.push({
            title: 'Operations',
            key: 'operations',
            dataIndex: 'id',
            render: (val, record, index) => {
                const {currentCount} = record;
                const harvestClick = (stage === PlantStageType.Harvest) ? onHarvestClick(record) : undefined;
                return <PlantDropdownMenu plantId={record.id} onTransfer={onTransferModalOpen(record)}
                          onHarvest={harvestClick} count={currentCount} onRefresh={onRefresh} />
            }
        })
    }

    const onNewTable = () => {
        const now = toFSDateTime(new Date());
        const tableName = `Table ${now}`
        insertPlantTable(tableName, room.id).then(r => {
            console.log(`new table ${tableName}`);
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const onTableNameSave = (tableId) => (name) => {
        updatePlantTableName(tableId, name).then(r => {
            console.log(`change name of ${tableId} to ${name}`);
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const onNewRow = (tableId) => () => {
        const now = toFSDateTime(new Date());
        const rowName = `Row ${now}`
        insertPlantRow(rowName, room.id, tableId).then(r => {
            console.log(`new row ${rowName}`);
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const onRowNameSave = (rowId) => (name) => {
        updatePlantRowName(rowId, name).then(r => {
            console.log(`change name of row ${rowId} to ${name}`);
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const onPlantMove = (rowId) => {
        // move all selected plants to the new row
        const promises = [];
        const {id: roomId} = room;
        for (const [plantId, isChecked] of Object.entries(checked)) {
            if (isChecked) {
                promises.push(updatePlantLocation(plantId, roomId, rowId))
            }
        }
        Promise.all(promises).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const getPlantsForRow = (rowId) => {
        return plants.filter(p => {
            return (p.rowId === rowId);
        });
    }

    const getUnassignedPlants = () => {
        return plants.filter(p => {
            return (rowMap[p.rowId] === undefined);
        })
    }

    const onHarvestModalOk = (notes, createdBy, date) => {
        // createdBy is a user, extract the id
        const {id} = createdBy;
        const {id: plantId} = focusPlant;
        harvestPlant(plantId, notes, id, date).then(r => {
            if (onRefresh) { onRefresh() }
            messageApi.open({
                type: 'success',
                content: `Plants Harvested`,
            });
        }).catch(e => {
            messageApi.open({
                type: 'error',
                content: `Harvest Plants Failed`,
            });
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
        setHarvestModalOpen(false);
    }

    const onHarvestModalCancel = () => {
        setHarvestModalOpen(false)
    }

    const onDeleteTable = (tableId) => () => {
        deletePlantTable(tableId).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {});
    }

    const onDeleteRow = (rowId) => () => {
        deletePlantRow(rowId).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {});
    }

    const onTransferOk = (numClones, selectedRoom) => {
        console.log(`transfer plant`);
        const room = roomMap[selectedRoom];
        const roomId = selectedRoom;
        const {type: roomType} = room;

        // the next stage will match the roomType of the destination room
        const nextStage = roomType;

        transferPlant(focusPlant.id, numClones, nextStage, roomId).then(r => {
            if (onRefresh) { onRefresh() }
        }).catch(e => {
            console.log(`transfer plants failed`);
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });

        setTransferModalOpen(false);
    }

    const onTransferAllOk = (selectedRoom) => {
        const room = roomMap[selectedRoom];
        const roomId = selectedRoom;
        const {type: roomType} = room;

        // the next stage will match the roomType of the destination room
        const nextStage = roomType;
        const promises = [];
        plants.forEach(p => {
            const {id, currentCount} = p;
            if (checked[p.id]) {
                promises.push(transferPlant(id, currentCount, nextStage, roomId));
            }
        });

        Promise.all(promises).then(r => {
            setChecked({})
            if (onRefresh) onRefresh();
        }).catch(e => {
            handleNoSession(e, onApiFail);
        });

        setAllModalOpen(false);
    }

    const onBatchChange = (val) => {
        // setBatch(val);
        const {id} = val;
        const promises = [];
        promises.push(insertRoomBatch(room.id, id));
        promises.push(updateBatch(id, {flowerRoomId: room.id}))
        Promise.all(promises).then(r => {
            if (onRefresh) { onRefresh() }
        }).catch(e => {
            handleNoSession(e, onApiFail);
        })
    }

    const clearBatch = () => {
        setBatch(undefined);
        deleteRoomBatch(room.id).then(r => {
            if (onRefresh) { onRefresh() }
        }).catch(e => {
            handleNoSession(e, onApiFail);
        })
    }

    const onStartDateChange = (dateString, dateJS) => {
        updateBatch(batch.id, {startTime: dateJS.toString(), flowerRoomId: room.id}).then(r => {
            if (onRefresh) { onRefresh(); }
        }).catch(e => {
            handleNoSession(e, onApiFail);
        });
    }

    const onHarvestAll = () => {
        setHarvestAllOpen(true);
    }

    const onHarvestAllOk = (notes, createdBy, date) => {
        // move all selected plants to the new row
        const promises = [];
        for (const [plantId, isChecked] of Object.entries(checked)) {
            if (isChecked) {
                promises.push(harvestPlant(plantId, notes, createdBy, date))
            }
        }
        Promise.all(promises).then(r => {
            if (onRefresh) onRefresh();
            messageApi.open({
                type: 'success',
                content: `Plants Harvested`,
            });
        }).catch(e => {
            messageApi.open({
                type: 'error',
                content: `Harvest Plants Failed`,
            });
            handleNoSession(e, onApiFail);
        });

        setHarvestAllOpen(false);
    }

    const onHarvestAllCancel = () => {
        setHarvestAllOpen(false);
    }

    let bulkEnabled = false;
    let earliestCreationTime;
    let numChecked = 0;
    let checkedCount = 0;
    for (const val of Object.values(checked)) {
        bulkEnabled |= val;
        if (val) numChecked++;
    }
    const pluralString = numChecked > 1 ? `s` : ``;

    for (const plant of plants) {
        if (checked[plant.id] === true) {
            // compute the earliest creation time for all selected plants
            const creationDate = new Date(plant.creationTime)
            if ((earliestCreationTime === undefined) || (creationDate < earliestCreationTime)) {
                earliestCreationTime = creationDate;
            }
            const {currentCount} = plant;
            checkedCount += currentCount;
        }
    }

    // disable tags if nothing is checked
    let tagDisabled = true;
    for (const val of Object.values(checked)) {
        if (val === true) {
            tagDisabled = false;
        }
    }

    if (room  === undefined) {
        return <div/>
    }

    let titleContent = <RoomNameWidget room={room} onApiFail={onApiFail} onRefresh={onRefresh} />;
    const unassignedPlants = getUnassignedPlants();

    let currentCount = 0;
    if (focusPlant) {
        currentCount = focusPlant.currentCount;
    }

    let roomOptions = [];

    if (Object.keys(roomsMap).length > 0) {
        // guard this section since roomMap might not be ready
        roomOptions = getPlantRoomTransferOptions("cultivation", roomsMap, room.id, stage, false);
    }

    return <div>
        {contextHolder}
        <TransferModal roomOptions={roomOptions} open={transferModalOpen} defaultCount={currentCount} onOk={onTransferOk} onCancel={onTransferCancel} max={currentCount} />
        <TransferAllModal roomOptions={roomOptions} open={allModalOpen} onOk={onTransferAllOk} onCancel={onAllModalCancel} />
        {(room.type !== PlantRoomType.Harvest) &&
            <RoomEnvironmentTab room={room} onRefresh={onRefresh} onApiFail={onApiFail}/>
        }
        {(room.type !== PlantRoomType.Harvest) &&
            <RoomLightTab rows={rows} plants={plants} onRefresh={onRefresh} onApiFail={onApiFail} />
        }
        <Card title={titleContent} style={cardStyle}>
            <HarvestModal user={session} open={harvestModalOpen} onOk={onHarvestModalOk} onCancel={onHarvestModalCancel} count={currentCount} />
            <HarvestModal user={session} open={harvestAllOpen} onOk={onHarvestAllOk} onCancel={onHarvestAllCancel} count={checkedCount} />
            {(numChecked > 0) &&
            <Row style={rowStyle}>
                <Col style={{...colStyle, color: '#108ee9'}}>{`${numChecked} plant${pluralString} selected`}</Col>
            </Row>
            }
            <Row style={rowStyle}>
                <Col style={colStyle}>
                    <Button onClick={onSelectAll}>Select All</Button>
                </Col>
                <Col style={colStyle}>
                    <Button onClick={onDeselectAll}>Select None</Button>
                </Col>
                <Col style={colStyle}>
                    <MovePlantWidget disabled={!bulkEnabled} onMove={ onPlantMove } rows={rows} />
                </Col>
                <Col style={colStyle}>
                    <TagSelectWidget plantId={Object.keys(checked)} onApiFail={onApiFail} refresh={refresh} onRefresh={onRefresh} disabled={tagDisabled} label={`Tag Selection`}/>
                </Col>
                <Col style={colStyle}>
                    <Button onClick={onAllModalOpen} disabled={!bulkEnabled}>Transfer</Button>
                </Col>
                {(room.type === PlantRoomType.Harvest) &&
                    <Col style={colStyle}>
                        <Button onClick={onHarvestAll} disabled={!bulkEnabled}>Harvest</Button>
                    </Col>
                }
            </Row>
            {(unassignedPlants.length > 0) && <div>
                <Row style={{...rowStyle, marginLeft:'7px'}}>
                    <b><Popover content={`These plants have not been assigned to a table`}>
                        Unassigned Plants
                    </Popover></b>
                </Row>
                <Table dataSource={unassignedPlants} columns={columns} pagination={false}/>
            </div>
            }
        </Card>
        {(room.type !== PlantRoomType.Harvest) &&
        <Card style={cardStyle} title={<b>{room.name} Batch Control</b>}>
            <Row style={rowStyle}>
                <Col span={12}>
                    <BatchSelectWidget onSelect={onBatchChange} selectedBatch={batch} onApiFail={onApiFail} refresh={refresh} displayType={"inline"} defaultActive={false} />
                </Col>
            </Row>
            {(batch !== undefined) && <div>
                <Row style={rowStyle}>
                    <Col span={2} style={{marginTop: '5px'}}><b>Start Time:</b></Col>
                    <Col>
                        <DatePickerWidget date={batch.startTime} placeholder={"Start Date"} onChange={onStartDateChange} />
                    </Col>
                </Row>
                <Row style={rowStyle}>
                    <Button onClick={clearBatch}>Clear Batch</Button>
                </Row>
            </div>
            }
            {(batch !== undefined) && <div>
                <Divider />
                <Row style={rowStyle}>
                    <b>New Plant</b>
                </ Row>
                <NewPlant roomId={room.id} batch={batch} io={io} />
            </div>
            }
        </Card>
        }
        {tables.map(table => {
            return <Card key={table.id} style={cardStyle} title={<NameChangeWidget name={table.name} onNameSave={onTableNameSave(table.id)} />} >
                <Row>
                    <Col span={2}>
                        <Button onClick={onNewRow(table.id)}>Add Row</Button>
                    </Col>
                    {(rows.filter(row => (row.tableId === table.id)).length === 0) &&
                        <Col>
                            <TrashWidget onDelete={onDeleteTable(table.id)} />
                        </Col>
                    }
                </Row>
                <Divider />
                {rows.filter(row => (row.tableId === table.id)).map(row => {
                    return <div key={row.id}>
                        <Row style={rowStyle}>
                            <Col>
                                <b>{<NameChangeWidget onNameSave={onRowNameSave(row.id)} name={row.rowName} />}</b>
                            </Col>
                            {(getPlantsForRow(row.id).length === 0) &&
                            <Col style={{marginLeft: '10px'}}>
                                <TrashWidget onDelete={onDeleteRow(row.id)} />
                            </Col>
                            }
                        </Row>
                        {(room.type === PlantRoomType.Harvest) &&
                            <MoistureWidget rowId={row.id} refresh={refresh} onRefresh={onRefresh} onApiFail={onApiFail} />
                        }
                        {(getPlantsForRow(row.id).length > 0) &&
                        <Table dataSource={getPlantsForRow(row.id)} columns={columns} pagination={false}/>
                        }
                        <Divider />
                    </div>
                })
                }
            </Card>
        })}
        <Card style={cardStyle} title={<b>Room Operations</b>}>
            <Button onClick={onNewTable}>New Table</Button>
        </Card>
    </div>
}